import type { CsrfManager } from '@ams/shared/auth';

export interface AuthScope {
  kind: 'global' | 'groups' | 'ou_subtree';
  groupIds?: string[];
  ouId?: string;
}

export interface AuthPayload {
  user: { email: string; displayName: string };
  privileges: string[];
  scopes: AuthScope[];
  sessionExpiresAt: string;
}

export class InvalidCredentialsError extends Error {
  constructor() {
    super('invalid credentials');
    this.name = 'InvalidCredentialsError';
  }
}

export class AccountInactiveError extends Error {
  constructor() {
    super('account not active');
    this.name = 'AccountInactiveError';
  }
}

export interface AuthServiceDeps {
  apiUrl: string;
  fetch?: typeof fetch;
  csrfManager?: Pick<CsrfManager, 'setFromResponse' | 'invalidate'>;
}

export interface AuthService {
  login(email: string, password: string): Promise<AuthPayload>;
  logout(): Promise<void>;
  verify(): Promise<AuthPayload | null>;
}

export function createAuthService(deps: AuthServiceDeps): AuthService {
  const doFetch = deps.fetch ?? fetch;
  const url = (path: string) => `${deps.apiUrl}${path}`;

  return {
    async login(email, password) {
      const res = await doFetch(url('/auth/login'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (res.status === 401) throw new InvalidCredentialsError();
      if (res.status === 403) throw new AccountInactiveError();
      if (!res.ok) throw new Error(`login failed: ${res.status}`);
      deps.csrfManager?.setFromResponse(res);
      return (await res.json()) as AuthPayload;
    },

    async logout() {
      const res = await doFetch(url('/auth/logout'), {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status !== 204 && !res.ok) {
        throw new Error(`logout failed: ${res.status}`);
      }
      deps.csrfManager?.invalidate();
    },

    async verify() {
      const res = await doFetch(url('/auth/verify'), {
        method: 'GET',
        credentials: 'include',
      });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`verify failed: ${res.status}`);
      deps.csrfManager?.setFromResponse(res);
      return (await res.json()) as AuthPayload;
    },
  };
}
